import React from 'react';
import { cn } from '@/utils/cn';
import { formatAccountId } from '@/utils/metaAds';
import { formatDateForDisplay } from '@/utils/format';
import type {
  MetaBidStrategy,
  MetaCampaignCreateStatus,
  MetaCampaignObjective,
  MetaSpecialAdCategory,
} from '@/services/api/metaAds';
import type { MetaBudgetMode } from './MetaBudgetStep';

interface MetaReviewStepProps {
  accountId: string | null;
  accountBusinessName: string | null;
  campaignName: string;
  objective: MetaCampaignObjective | null;
  status: MetaCampaignCreateStatus;
  bidStrategy: MetaBidStrategy;
  budgetMode: MetaBudgetMode;
  budgetAmount: string;
  startDate: string;
  stopDate: string;
  hasEndDate: boolean;
  specialAdCategories: MetaSpecialAdCategory[];
  onEditStep: (step: number) => void;
}

const OBJECTIVE_LABELS: Record<MetaCampaignObjective, string> = {
  OUTCOME_SALES: 'Sales',
  OUTCOME_TRAFFIC: 'Traffic',
  OUTCOME_AWARENESS: 'Awareness',
  OUTCOME_ENGAGEMENT: 'Engagement',
  OUTCOME_LEADS: 'Leads',
  OUTCOME_APP_PROMOTION: 'App promotion',
};

const STATUS_LABELS: Record<MetaCampaignCreateStatus, string> = {
  PAUSED: 'Paused',
  ACTIVE: 'Active',
};

const BID_STRATEGY_LABELS: Record<MetaBidStrategy, string> = {
  LOWEST_COST_WITHOUT_CAP: 'Lowest cost',
  LOWEST_COST_WITH_BID_CAP: 'Lowest cost with bid cap',
  COST_CAP: 'Cost cap',
  LOWEST_COST_WITH_MIN_ROAS: 'Minimum ROAS',
};

const CATEGORY_LABELS: Record<MetaSpecialAdCategory, string> = {
  NONE: 'None of the above',
  HOUSING: 'Housing',
  EMPLOYMENT: 'Employment',
  CREDIT: 'Credit',
  ISSUES_ELECTIONS_POLITICS: 'Social issues, elections, or politics',
};

/**
 * Step 6 — review every section before submit. Mirrors Google's
 * `CampaignCreation/steps/ReviewStep` shape: per-domain bordered cards
 * (Account / Campaign Basics / Budget & Bidding / Schedule / Categories),
 * one Edit button per section that jumps the wizard back to that step,
 * label-on-left + value-on-right semantic markup (`<dl>`/`<dt>`/`<dd>`)
 * with desktop right-alignment, and a yellow "Before You Create" notice
 * at the bottom that conditionally warns when ACTIVE is selected.
 */
export const MetaReviewStep: React.FC<MetaReviewStepProps> = ({
  accountId,
  accountBusinessName,
  campaignName,
  objective,
  status,
  bidStrategy,
  budgetMode,
  budgetAmount,
  startDate,
  stopDate,
  hasEndDate,
  specialAdCategories,
  onEditStep,
}) => {
  const budgetValue = parseFloat(budgetAmount) || 0;
  const budgetDisplay =
    budgetValue > 0
      ? `$${budgetValue.toFixed(2)} ${budgetMode === 'daily' ? '/ day' : 'lifetime'}`
      : 'Not set';
  // For daily budgets, surface an estimated monthly spend as a `detail` row
  // — mirrors Google's "Estimated 30 days: $X" sub-line under Daily Budget.
  const budgetDetail =
    budgetMode === 'daily' && budgetValue > 0
      ? `Estimated 30 days: $${(budgetValue * 30).toFixed(2)}`
      : undefined;

  const stopExpected = budgetMode === 'lifetime' || hasEndDate;

  const categoriesDisplay =
    specialAdCategories.length === 0
      ? 'Not declared'
      : specialAdCategories.map((c) => CATEGORY_LABELS[c]).join(', ');

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Review Your Campaign
        </h3>
        <p className="text-sm text-gray-600">
          Review your campaign settings before creating. Click any section to edit.
        </p>
      </div>

      {/* Account section intentionally has no Edit button. Changing the
          account mid-wizard would invalidate every later step (the budget
          rules, the available creative library, etc.), so the user must
          pick correctly in the OmniAccountPicker / Step 1 or close and
          start over. */}
      <ReviewSection
        title="Meta Ads Account"
        items={[
          { label: 'Business', value: accountBusinessName ?? 'Unnamed business' },
          // formatAccountId strips the `act_` namespace for display so it
          // matches what the user sees in Meta Ads Manager URLs, support
          // tickets, etc. The wire-level value (with prefix) still flows
          // through unchanged on submit.
          { label: 'Account ID', value: accountId ? formatAccountId(accountId) : 'Not selected' },
        ]}
      />

      <ReviewSection
        title="Campaign Basics"
        onEdit={() => onEditStep(2)}
        items={[
          { label: 'Campaign Name', value: campaignName || 'Not set' },
          { label: 'Objective', value: objective ? OBJECTIVE_LABELS[objective] : 'Not set' },
          { label: 'Initial Status', value: STATUS_LABELS[status] },
        ]}
      />

      <ReviewSection
        title="Budget & Bidding"
        onEdit={() => onEditStep(3)}
        items={[
          { label: 'Budget', value: budgetDisplay, detail: budgetDetail },
          { label: 'Bid Strategy', value: BID_STRATEGY_LABELS[bidStrategy] },
        ]}
      />

      <ReviewSection
        title="Schedule"
        onEdit={() => onEditStep(4)}
        items={[
          {
            label: 'Start Date',
            // `formatDateForDisplay` parses YYYY-MM-DD as a local date so
            // users west of UTC don't see the previous day in the Review
            // summary (the raw `new Date('2026-06-13')` would parse as
            // UTC midnight and render as Jun 12 in PT).
            value: startDate
              ? formatDateForDisplay(startDate)
              : 'Not set',
          },
          {
            label: 'Stop Date',
            value: stopExpected
              ? (stopDate
                  ? formatDateForDisplay(stopDate)
                  : 'Not set')
              : 'No end date',
          },
        ]}
      />

      <ReviewSection
        title="Special Ad Categories"
        onEdit={() => onEditStep(5)}
        items={[
          { label: 'Declaration', value: categoriesDisplay },
        ]}
      />

      {/* Before You Create — yellow notice matching Google's ReviewStep. */}
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <div className="flex">
          <svg
            className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-yellow-800">
              Before You Create
            </h4>
            <ul className="mt-1 text-xs text-yellow-700 list-disc list-inside space-y-1">
              <li>
                This will create a campaign shell. You'll need to add ad sets,
                ads, and creatives separately.
              </li>
              <li>Campaign will be created with status: {STATUS_LABELS[status]}</li>
              {status === 'ACTIVE' && (
                <li className="font-semibold">
                  The campaign won't actually serve or bill until at least one
                  ad set + ad + creative exists. Add them right after to start
                  serving.
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ReviewSectionProps {
  title: string;
  /** Omit to render a read-only section (no Edit button). Used for the
   *  Account section, which can't be changed mid-wizard. */
  onEdit?: () => void;
  items: Array<{
    label: string;
    value: string;
    detail?: string;
    highlight?: boolean;
  }>;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ title, onEdit, items }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium focus:outline-none focus:underline"
          >
            Edit
          </button>
        )}
      </div>
      <dl className="space-y-2">
        {items.map((item) => (
          <div
            key={item.label}
            className="flex flex-col sm:flex-row sm:justify-between text-sm gap-1 sm:gap-2"
          >
            <dt className="text-gray-600">{item.label}:</dt>
            <dd className="font-medium text-gray-900 sm:text-right">
              <span className={cn(item.highlight && 'text-yellow-600')}>
                {item.value}
              </span>
              {item.detail && (
                <span className="block text-xs text-gray-500 mt-0.5">
                  {item.detail}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};
